import { Injectable, inject } from '@angular/core';
import { APP_CONFIG } from '@core/config/app-config.token';
import { StoredSession } from './token-pair';

/**
 * Persists the admin session (access + refresh token) in sessionStorage: cleared when the tab
 * closes, not shared across tabs, never localStorage. Swap this for an httpOnly refresh cookie once
 * the API supports it; callers only depend on this class.
 */
@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  private readonly prefix = inject(APP_CONFIG).storagePrefix;
  private readonly key = `${this.prefix}.admin_session`;
  /** Key used before refresh tokens existed; removed on every write/clear. */
  private readonly legacyKey = `${this.prefix}.access_token`;

  read(): StoredSession | null {
    try {
      const raw = sessionStorage.getItem(this.key);
      return raw ? parseSession(raw) : null;
    } catch {
      return null;
    }
  }

  write(session: StoredSession): void {
    try {
      sessionStorage.removeItem(this.legacyKey);
      sessionStorage.setItem(this.key, JSON.stringify(session));
    } catch {
      // Storage unavailable (private mode / quota): the session stays in memory only.
    }
  }

  clear(): void {
    try {
      sessionStorage.removeItem(this.key);
      sessionStorage.removeItem(this.legacyKey);
    } catch {
      // ignore
    }
  }
}

function parseSession(raw: string): StoredSession | null {
  try {
    const value = JSON.parse(raw) as Partial<StoredSession> | null;
    if (
      value &&
      typeof value.accessToken === 'string' &&
      typeof value.refreshToken === 'string' &&
      typeof value.accessTokenExpiresAt === 'number' &&
      typeof value.refreshTokenExpiresAt === 'number'
    ) {
      return {
        accessToken: value.accessToken,
        accessTokenExpiresAt: value.accessTokenExpiresAt,
        refreshToken: value.refreshToken,
        refreshTokenExpiresAt: value.refreshTokenExpiresAt,
      };
    }
    return null;
  } catch {
    return null;
  }
}
